import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Component, ContentChild, ContentChildren, Directive, ElementRef, Input, OnChanges, OnInit, QueryList, SimpleChanges, TemplateRef } from '@angular/core';
import { FormArray, FormControl, FormGroup, NonNullableFormBuilder } from '@angular/forms';
import { MatIconModule } from '@angular/material/icon';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { AvatarBlockModule } from '../avatar-block/avatar-block.module';
import { MatInputModule } from '@angular/material/input';
import { NgForOf, NgIf } from '@angular/common';
import { CounterPipe } from '../../../../pipes/collection/counter.pipe';
import { ButtonSpacerDirective } from '../directives/button.directive';
import { EmptyStateModule } from '../empty-state/empty-state.module';
import { SharedModule } from '../../../shared/shared.module';
import { FlexSpacerDirective } from '../directives/flex-spacer.directive';
import { EmptyStateComponent } from '../empty-state/empty-state.component';
import { AnimatedDelayDirective, AnimatedDirective } from '../directives/animated.directive';
import { debounceTime } from 'rxjs/operators';
import { MatTableModule } from '@angular/material/table';
import { GrowlerService } from '../../../../services/growler.service';
import { originalOrderFunction } from '../../../../functions/original-order';
import { SidenavContentModule } from '../sidenav/sidenav-content/sidenav-content.module';


@Directive({
    selector: 'toggle-list-group',
    standalone: true,
    host: { class: 'toggle-list-group' },
})
export class ToggleListGroupDirective<T>
{
    @Input() groupKey = 'group';
    @Input() groupPredicate: (item: T) => boolean;

    constructor(public _el: ElementRef) {
    }

    get label() {
        return this._el.nativeElement.textContent;
    }
}

export interface ToggleListGroups<T> {
    groups: {
        [prop:string]: {
            label: string;
            predicate: (item: T) => boolean;
        };
    };
    map: Map<string, number>;
}

export interface SelectedFormGroup<T>
{
    item: FormControl<T>;
    group: FormControl<string>;
    selected: FormControl<boolean>;
    visible: FormControl<boolean>;
}

export interface SelectedFormValue<T>
{
    item: T;
    selected: boolean;
    visible: boolean;
}

@UntilDestroy()
@Component({
    selector: 'app-toggle-list',
    templateUrl: './toggle-list.component.html',
    standalone: true,
    imports: [
        MatFormFieldModule,
        MatSlideToggleModule,
        AvatarBlockModule,
        MatInputModule,
        NgForOf,
        CounterPipe,
        ButtonSpacerDirective,
        EmptyStateModule,
        NgIf,
        SharedModule,
        FlexSpacerDirective,
        MatIconModule,
        AnimatedDirective,
        AnimatedDelayDirective,
        MatTableModule,
        SidenavContentModule,
    ],
})
export class ToggleListComponent<T> implements OnChanges, OnInit
{
    @Input() data: T[];
    @Input() preselectedIds: number[];
    @Input() requiredSelection = true;

    // initial check for preselection
    @Input() preselected: (item: T) => boolean;

    // initial check for disabled controls based on criteria
    @Input() disabled: (item: T) => boolean;

    // search comparison for filtering results
    @Input() search: (item: T, term: string) => boolean;

    @ContentChild(EmptyStateComponent) emptyStateRef!: EmptyStateComponent;
    @ContentChild(TemplateRef) listItemRef: TemplateRef<T>;

    formGroup = this._fb.group({
        filterTerm: { value: '', disabled: true },
        selectAll: { value: false, disabled: true },
        data: this._fb.array<FormGroup<SelectedFormGroup<T>>>([]),
    });
    totalItems = 0;
    isLoadingInitialResults = true;
    itemsAnimated = true;

    readonly originalOrder = originalOrderFunction;
    readonly listGroups: ToggleListGroups<T> = {
        groups: {},
        map: new Map(),
    };

    constructor(
            private _fb: NonNullableFormBuilder,
            private _growler: GrowlerService,
    ) {
    }

    @ContentChildren(ToggleListGroupDirective) set listGroupRefs(groups: QueryList<ToggleListGroupDirective<T>>) {
        if (groups && groups.length > 0) {
            groups.forEach((group: ToggleListGroupDirective<T>, index: number) => {
                this.listGroups.groups[group.groupKey] = {
                    label: group.label,
                    predicate: group.groupPredicate || (() => true),
                };
                this.listGroups.map.set(group.groupKey, index);
            });
        }
    }

    get items() {
        return this.formGroup.get('data') as FormArray<FormGroup<SelectedFormGroup<T>>>;
    }

    get filtered() {
        return [...this.items.value].filter((value: SelectedFormValue<T>) => !!value.visible);
    }

    get combinedValue() {
        return [...this.items.value].filter((value: SelectedFormValue<T>) => !!value.selected)
                .map((value: SelectedFormValue<T>) => value.item);
    }

    get validate() {
        if (this.combinedValue.length === 0 && this.requiredSelection) {
            this.formGroup.markAsTouched();
            this.formGroup.setErrors({ required: true });
            this._growler.oops('Please double check required field');
            return false;
        }

        return true;
    }

    ngOnChanges({ data }: SimpleChanges) {
        if (data.currentValue) {
            this.totalItems = data.currentValue?.length;

            if (this.totalItems > 0) {
                for (const item of data.currentValue) {
                    this._addToggleListItem(item);
                }

                // using groups/grouping? relative array sort here to visually separate in list template
                if (this.listGroups.map.size > 0) {
                    this.items.controls.sort((a, b) => {
                        const indexA = this.listGroups.map.get(a.get('group').value);
                        const indexB = this.listGroups.map.get(b.get('group').value);
                        return indexA - indexB;
                    });
                }

                // all items initially preselected? toggle select-all to true
                if (this.preselectedIds?.length === this.totalItems) {
                    this.formGroup.get('selectAll').setValue(true, { emitEvent: false });
                }

                // select-all: if all active items get checked, toggle select-all true
                this.items.valueChanges.pipe(untilDestroyed(this)).subscribe({
                    next: () => {
                        const selectAllValue = this.combinedValue.length === this.totalItems;
                        this.formGroup.get('selectAll').setValue(selectAllValue, { emitEvent: false });
                        this.formGroup.setErrors(null);
                    }
                });

                // enable list controls for use
                this.formGroup.get('filterTerm').enable({ emitEvent: false });
                this.formGroup.get('selectAll').enable({ emitEvent: false });
            }

            this.isLoadingInitialResults = false;
        }
    }

    ngOnInit() {
        this.formGroup.get('selectAll').valueChanges.pipe(untilDestroyed(this)).subscribe({
            next: (allSelected) => {
                for (const control of this.items.controls.filter((c) => c.enabled)) {
                    control.get('selected').setValue(allSelected, { emitEvent: false });
                }
            },
        });

        this.formGroup.get('filterTerm').valueChanges.pipe(debounceTime(500), untilDestroyed(this)).subscribe({
            next: (value) => {
                this.itemsAnimated = false;

                if (value.trim().length > 0) {
                    this.emptyStateRef.searchTerm = value;
                }

                const filter = value.trim().toLowerCase();
                for (const control of this.items.controls) {
                    const item = control.get('item').value;
                    const visible = this.search ? this.search(item, filter) : item['name'].trim().toLowerCase().indexOf(filter) > -1;
                    control.get('visible').setValue(visible, { emitEvent: false });
                }

                if (value.trim() === '') {
                    this.emptyStateRef.searchTerm = '';
                }
            },
        });
    }

    private _addToggleListItem(item: T) {
        // toggle list control to hold data and current state
        const control: FormGroup<SelectedFormGroup<T>> = this._fb.group({
            item: this._fb.control(item),
            group: [null],
            selected: [this.preselected ? this.preselected(item) : this.preselectedIds?.includes(item['id'])],
            visible: [true],
        });

        // is slide-toggle item initially disabled in list?
        if (this.disabled && this.disabled(item)) {
            control.get('selected').setValue(false);
            control.disable();

            this.totalItems--;
        }

        // handle list groups/grouping with control value (will be used to sort in template)
        for (const group in this.listGroups.groups) {
            if (this.listGroups.groups[group] && !control.get('group').value && this.listGroups.groups[group].predicate(item)) {
                control.get('group').setValue(group);
            }
        }

        this.items.push(control);
    }
}
